import { MenuItem } from '@headlessui/react';
import classNames from 'classnames';
import { forwardRef, FunctionComponent, MouseEventHandler, SVGProps, memo, useMemo } from 'react';
import { HeroIcon } from '../../types';

interface CommonProps {
  icon:
    | HeroIcon
    | FunctionComponent<
        SVGProps<SVGSVGElement> & {
          title?: string;
        }
      >;
  idle?: boolean;
  selectStyle?: 'primary' | 'secondary';
  activeLevel?: number;
  disabled?: boolean;
  onClick?: MouseEventHandler<HTMLDivElement>;
  onDoubleClick?: MouseEventHandler<HTMLDivElement>;
}

interface SelectItemContentProps extends CommonProps {
  active?: boolean;
  className?: string;
  children?: React.ReactNode;
}

const SelectItemContent_ = forwardRef<HTMLDivElement, SelectItemContentProps>(({ icon: Icon, children, active, activeLevel, selectStyle, idle, disabled, onClick, onDoubleClick, className: propsClassName, ...props }, ref) => {
  const className = useMemo(() => {
    if (selectStyle === 'secondary')
      return classNames({
        'border border-eerie-black bg-neon-green-300 dark:border-black dark:bg-eerie-black dark:text-white': active && activeLevel !== 1,
        'bg-neon-green-200 dark:bg-eerie-black dark:text-white': active && activeLevel === 1,
        'text-gray-500': idle || disabled,
        'cursor-not-allowed': disabled,
        'group hover:border border:eerie-black hover:bg-neon-green-300 dark:hover:bg-eerie-black hover:border-black dark:text-white': !disabled,
      });
    return classNames({
      'bg-neon-green-300 dark:bg-black': active && activeLevel !== 1,
      'bg-neon-green-200 dark:bg-eerie-black': active && activeLevel === 1,
      'text-gray-500': idle || disabled,
      'cursor-not-allowed': disabled,
      'hover:bg-neon-green-300 dark:hover:bg-black group': !disabled,
    });
  }, [active, activeLevel, disabled, idle, selectStyle]);

  return (
    <div
      ref={ref}
      className={classNames(`flex w-full items-center rounded-md px-2 py-2 text-sm border border-transparent cursor-pointer select-none`, className, propsClassName)}
      onClick={disabled ? undefined : onClick}
      onDoubleClick={disabled ? undefined : onDoubleClick}
      {...props}
    >
      <Icon className={'w-5 h-5 mr-2 group-hover:text-dark-green-800 dark:group-hover:text-neon-green-300 flex-shrink-0'} />
      {children}
    </div>
  );
});
SelectItemContent_.displayName = 'SelectItemContent';
export const SelectItemContent = SelectItemContent_;

const SelectItem_: React.FC2<CommonProps> = ({ icon, children, idle, activeLevel, disabled, ...props }) => {
  return (
    <MenuItem {...props}>
      {({ focus }) => (
        <SelectItemContent icon={icon} active={focus} activeLevel={activeLevel} idle={idle} disabled={disabled}>
          {children}
        </SelectItemContent>
      )}
    </MenuItem>
  );
};
export const SelectItem = memo(SelectItem_);
